import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ClipboardCheck, Eye, Truck, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ogclTransferService from '../../services/ogclTransferService.js';
import OgclReviewConfirmModal from './OgclReviewConfirmModal.js';
import OgclUnloadNoteModal from './OgclUnloadNoteModal.js';
import OgclShipmentActionsMenu from './OgclShipmentActionsMenu.js';
import { formatUsernameLocation } from '../../utils/userDisplay';
import { ITCL_REQUEST_DETAIL_ERROR } from './itclRequestUi.js';
import { isItclCompanyAdmin, shipmentOnMlSheet } from './itclShipmentActions.js';

const badgeClass = (status) => ({
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-green-50 text-green-700 border-green-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
  Booked: 'bg-blue-50 text-blue-700 border-blue-200',
  BOOKED: 'bg-blue-50 text-blue-700 border-blue-200',
  'In Transit': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Dispatched: 'bg-purple-50 text-purple-700 border-purple-200',
}[status] || 'bg-gray-50 text-gray-700 border-gray-200');

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const paymentLabel = (mode) => {
  const key = String(mode || '').toLowerCase();
  if (key === 'to_pay') return 'To Pay';
  if (key === 'paid') return 'Paid';
  if (key === 'credit' || key === 'on_account') return 'Credit';
  return mode || '—';
};

const DetailItem = ({ label, children }) => (
  <div className="min-w-0 rounded-lg bg-gray-50/80 px-3 py-2.5 sm:bg-transparent sm:px-0 sm:py-0">
    <p className="text-[10px] sm:text-xs uppercase tracking-wide text-gray-500 mb-0.5 sm:mb-1">{label}</p>
    <div className="text-xs sm:text-sm font-medium text-secondary break-words">{children || '—'}</div>
  </div>
);

const ViewOgclTransferRequest = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [unloadingId, setUnloadingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [request, setRequest] = useState(null);
  const [reviewModal, setReviewModal] = useState({ open: false, action: 'approve' });
  const [noteModal, setNoteModal] = useState({ open: false, action: 'unload', shipmentIds: [] });
  const role = user?.role || user?.role_name;
  const canReview = Boolean(request?.can_review);
  const isCompanyAdmin = isItclCompanyAdmin(role);

  const loadRequest = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ogclTransferService.get(requestId);
      const data = response.data?.data || response.data;
      if (!data?.request_id) {
        throw new Error('Request not found');
      }
      setRequest(data);
    } catch (error) {
      Alert.error(error.response?.data?.error || ITCL_REQUEST_DETAIL_ERROR);
      navigate('/ogcl-transfer-requests', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate, requestId]);

  useEffect(() => {
    loadRequest();
  }, [loadRequest]);

  const openReview = (action) => setReviewModal({ open: true, action });
  const closeReview = () => {
    if (acting) return;
    setReviewModal({ open: false, action: 'approve' });
  };

  const confirmReview = async (remarks) => {
    if (!request?.request_id) return;
    try {
      setActing(true);
      await ogclTransferService[reviewModal.action](request.request_id, { remarks });
      Alert.success(`Request ${reviewModal.action === 'approve' ? 'approved' : 'rejected'}`);
      setReviewModal({ open: false, action: 'approve' });
      await loadRequest();
    } catch (error) {
      Alert.error(error.response?.data?.error || `Failed to ${reviewModal.action} request`);
    } finally {
      setActing(false);
    }
  };

  const markUnloaded = (shipmentId) => {
    if (!request?.request_id || !shipmentId) return;
    setNoteModal({ open: true, action: 'unload', shipmentIds: [shipmentId] });
  };

  const markAllUnloaded = () => {
    const pending = (request?.shipments || [])
      .filter((row) => !row.unloaded && row.status !== 'NOT_FOUND' && !shipmentOnMlSheet(row))
      .map((row) => row.shipment_id);
    if (!pending.length || !request?.request_id) {
      Alert.error('No shipments are available to unload. Unload is blocked after an ML sheet is created.');
      return;
    }
    setNoteModal({ open: true, action: 'unload', shipmentIds: pending });
  };

  const revertUnload = (shipmentId) => {
    if (!request?.request_id || !shipmentId) return;
    setNoteModal({ open: true, action: 'revert', shipmentIds: [shipmentId] });
  };

  const removeShipment = (shipmentId) => {
    if (!request?.request_id || !shipmentId) return;
    setNoteModal({ open: true, action: 'remove', shipmentIds: [shipmentId] });
  };

  const markNote = (shipmentId) => {
    if (!request?.request_id || !shipmentId) return;
    setNoteModal({ open: true, action: 'note', shipmentIds: [shipmentId] });
  };

  const closeNoteModal = () => {
    if (unloadingId) return;
    setNoteModal({ open: false, action: 'unload', shipmentIds: [] });
  };

  const confirmNoteAction = async (note) => {
    const ids = noteModal.shipmentIds || [];
    if (!request?.request_id || !ids.length) return;
    if (!String(note || '').trim()) {
      Alert.error('Note is required');
      return;
    }
    const isRevert = noteModal.action === 'revert';
    const isRemove = noteModal.action === 'remove';
    const isNote = noteModal.action === 'note';
    try {
      setUnloadingId(ids.length === 1 ? ids[0] : 'all');
      const payload = { shipment_ids: ids, note: note.trim() };
      const response = isRemove
        ? await ogclTransferService.revert(request.request_id, payload)
        : isRevert
          ? await ogclTransferService.undoUnload(request.request_id, payload)
          : isNote
            ? await ogclTransferService.note(request.request_id, payload)
            : await ogclTransferService.unload(request.request_id, payload);
      const data = response.data?.data || response.data;
      if (data?.request_id) setRequest(data);
      Alert.success(
        isRemove
          ? `${ids.length === 1 ? ids[0] : `${ids.length} shipments`} removed from this ITCL request`
          : isRevert
            ? `${ids.length === 1 ? ids[0] : `${ids.length} shipments`} reverted to pending unload`
            : isNote
              ? `Note saved for ${ids.length === 1 ? ids[0] : 'selected shipments'}`
              : (ids.length === 1 ? `${ids[0]} marked unloaded` : 'All shipments marked unloaded'),
      );
      setNoteModal({ open: false, action: 'unload', shipmentIds: [] });
    } catch (error) {
      Alert.error(
        error.response?.data?.error
        || (isRemove
          ? 'Failed to remove shipment'
          : isRevert
            ? 'Failed to revert unload'
            : isNote
              ? 'Failed to save note'
              : 'Failed to mark shipment unloaded'),
      );
    } finally {
      setUnloadingId(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-20">
          <Loader fullScreen={false} size="default" message="Loading ITCL request..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!request) return null;

  const shipments = Array.isArray(request.shipments) ? request.shipments : [];
  const revertedShipments = Array.isArray(request.reverted_shipments) ? request.reverted_shipments : [];
  const totalFreight = shipments.reduce((sum, row) => sum + Number(row.freight_charges || 0), 0);
  const canManageUnload = Boolean(
    request.can_manage_unload || request.is_destination_user || isCompanyAdmin
  );
  const showChecklist = request.status === 'APPROVED' && canManageUnload;
  const allUnloaded = Boolean(request.all_unloaded)
    || (shipments.length > 0 && shipments.every((row) => row.unloaded));

  const renderShipmentActions = (shipment, menuKey) => (
    shipment.status === 'NOT_FOUND' ? null : (
      <OgclShipmentActionsMenu
        menuKey={menuKey}
        openMenuId={openMenuId}
        setOpenMenuId={setOpenMenuId}
        shipment={shipment}
        role={role}
        canManage={showChecklist}
        acting={unloadingId != null}
        onView={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
        onUnload={() => markUnloaded(shipment.shipment_id)}
        onRevert={() => revertUnload(shipment.shipment_id)}
        onRemove={() => removeShipment(shipment.shipment_id)}
        onNote={() => markNote(shipment.shipment_id)}
      />
    )
  );

  return (
    <DashboardLayout>
      <div className="w-full max-w-full min-w-0 mx-auto overflow-x-hidden space-y-4 sm:space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between min-w-0">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-secondary flex items-start gap-2 min-w-0">
              <Truck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0 mt-0.5" />
              <span className="break-all leading-snug">{request.request_id}</span>
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
              {request.from_hub_name || request.from_hub_id} → {request.to_hub_name || request.to_hub_id}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end shrink-0 w-full sm:w-auto">
            <span className={`inline-flex px-2.5 sm:px-3 py-1 rounded-full border text-[10px] sm:text-xs font-semibold ${badgeClass(request.status)}`}>
              {request.status}
            </span>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              mobileIconOnly
              className="shrink-0"
              onClick={() => navigate('/ogcl-transfer-requests')}
            >
              Back
            </Button>
            {request.is_destination_user && request.status === 'APPROVED' && (
              <Button
                variant="primary"
                icon={Truck}
                disabled={!allUnloaded}
                className="flex-1 sm:flex-none min-w-0"
                onClick={() => navigate('/ml-sheets/add')}
              >
                Create ML / Dispatch
              </Button>
            )}
            {canReview && request.status === 'PENDING' && (
              <>
                <Button
                  variant="primary"
                  icon={CheckCircle2}
                  disabled={acting}
                  className="flex-1 sm:flex-none min-w-0"
                  onClick={() => openReview('approve')}
                >
                  Approve
                </Button>
                <Button
                  variant="danger"
                  icon={XCircle}
                  disabled={acting}
                  className="flex-1 sm:flex-none min-w-0"
                  onClick={() => openReview('reject')}
                >
                  Reject
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-5 min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-secondary mb-3 sm:mb-4">Request Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4 min-w-0">
            <DetailItem label="From Hub">{request.from_hub_name || request.from_hub_id}</DetailItem>
            <DetailItem label="To Hub">{request.to_hub_name || request.to_hub_id}</DetailItem>
            <DetailItem label="Shipments">{request.shipment_count ?? shipments.length}</DetailItem>
            <DetailItem label="Total Freight">{formatMoney(totalFreight)}</DetailItem>
            <DetailItem label="Vehicle">
              {request.vehicle_number}
              {request.is_external_vehicle ? ' (Other)' : ''}
            </DetailItem>
            <DetailItem label="Driver">{request.driver_name}</DetailItem>
            <DetailItem label="Requested By">
              {request.requested_by_display
                || formatUsernameLocation(
                  request.requested_by_name || request.requested_by_username,
                  request.requested_by_hub_name,
                  request.requested_by
                )}
            </DetailItem>
            <DetailItem label="Created At">{formatDateTime(request.created_at)}</DetailItem>
            <DetailItem label="Reviewed At">{formatDateTime(request.reviewed_at)}</DetailItem>
            <DetailItem label="Reviewed By">
              {request.reviewer_display
                || formatUsernameLocation(
                  request.reviewer_name || request.reviewer_username,
                  request.reviewer_hub_name,
                  request.reviewer_user_id || '—'
                )}
            </DetailItem>
            <DetailItem label="Remarks">{request.remarks}</DetailItem>
            <DetailItem label="Review Remarks">{request.review_remarks}</DetailItem>
          </div>
        </div>

        {showChecklist && (
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-5 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <h2 className="text-base sm:text-lg font-semibold text-secondary flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 shrink-0" />
                Unload checklist
              </h2>
              <Button
                size="sm"
                variant="secondary"
                disabled={allUnloaded || unloadingId === 'all'}
                loading={unloadingId === 'all'}
                onClick={markAllUnloaded}
              >
                Mark all unloaded
              </Button>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-3">
              Unload (with a note) is available before unload. After unload, Hub Admin can Revert
              it back to pending unload. Admin always has Unload and Revert. Remove from request
              takes the GC off this ITCL request. Unload is blocked after an ML sheet is created.
            </p>
            <ul className="space-y-2">
              {shipments.map((shipment) => (
                <li
                  key={shipment.shipment_id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-secondary break-all">{shipment.shipment_id}</p>
                    <p className="text-xs text-gray-500 break-words">
                      {shipment.sender_location || '—'} → {shipment.receiver_location || '—'}
                    </p>
                    {shipment.unload_note ? (
                      <p className="text-xs text-gray-600 mt-1">Note: {shipment.unload_note}</p>
                    ) : null}
                    {shipmentOnMlSheet(shipment) ? (
                      <p className="text-xs text-amber-800 mt-1">On ML sheet — unload is blocked</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {shipment.unloaded ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                        <CheckCircle2 className="w-4 h-4" /> Unloaded
                      </span>
                    ) : null}
                    {renderShipmentActions(shipment, `check-${shipment.shipment_id}`)}
                  </div>
                </li>
              ))}
            </ul>
            {!allUnloaded && (
              <p className="mt-3 text-xs text-amber-800">
                Dispatch is enabled after every shipment on this request is unloaded.
              </p>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm overflow-hidden min-w-0">
          <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex items-center justify-between gap-3 min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-secondary truncate">Shipments</h2>
            <span className="text-xs sm:text-sm text-gray-500 shrink-0">
              {shipments.length} item{shipments.length === 1 ? '' : 's'}
            </span>
          </div>

          {!shipments.length ? (
            <div className="px-4 py-12 text-center text-gray-500 text-sm">
              No shipments linked to this transfer request.
            </div>
          ) : (
            <>
              <div className="hidden md:block w-full max-w-full overflow-x-auto overscroll-x-contain">
                <table className="w-full min-w-[880px] text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Shipment</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">From → To</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Sender / Receiver</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Payment</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Freight</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Status</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">ITCL</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {shipments.map((shipment) => (
                      <tr key={shipment.shipment_id} className="align-top hover:bg-gray-50/80">
                        <td className="px-3 lg:px-4 py-3 font-medium text-secondary break-all">{shipment.shipment_id}</td>
                        <td className="px-3 lg:px-4 py-3 break-words">
                          {shipment.sender_location || '—'} → {shipment.receiver_location || '—'}
                        </td>
                        <td className="px-3 lg:px-4 py-3 break-words">
                          <div>{shipment.sender_name || '—'}</div>
                          <div className="text-gray-500">{shipment.receiver_name || '—'}</div>
                        </td>
                        <td className="px-3 lg:px-4 py-3 whitespace-nowrap">{paymentLabel(shipment.mode_of_payment)}</td>
                        <td className="px-3 lg:px-4 py-3 whitespace-nowrap">{formatMoney(shipment.freight_charges)}</td>
                        <td className="px-3 lg:px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded-full border text-xs font-semibold ${badgeClass(shipment.status)}`}>
                            {shipment.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 lg:px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded-full border text-xs font-semibold ${badgeClass(shipment.ogcl_transfer_status)}`}>
                            {shipment.ogcl_transfer_status || '—'}
                          </span>
                        </td>
                        <td className="px-3 lg:px-4 py-3">
                          {renderShipmentActions(shipment, shipment.shipment_id)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden p-3 space-y-3">
                {shipments.map((shipment) => (
                  <div
                    key={shipment.shipment_id}
                    className="rounded-xl border border-gray-100 border-l-4 border-l-orange-400 bg-white shadow-sm p-3 min-w-0"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0">
                        <div className="font-bold text-secondary text-sm break-all">{shipment.shipment_id}</div>
                        <p className="text-xs text-gray-600 mt-1 break-words">
                          {shipment.sender_location || '—'} → {shipment.receiver_location || '—'}
                        </p>
                      </div>
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold shrink-0 ${badgeClass(shipment.status)}`}>
                        {shipment.status || '—'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Sender</span>
                        <p className="font-medium text-gray-800 break-words">{shipment.sender_name || '—'}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Receiver</span>
                        <p className="font-medium text-gray-800 break-words">{shipment.receiver_name || '—'}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Payment</span>
                        <p className="font-medium text-gray-800">{paymentLabel(shipment.mode_of_payment)}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Freight</span>
                        <p className="font-medium text-gray-800">{formatMoney(shipment.freight_charges)}</p>
                      </div>
                      <div className="min-w-0 col-span-2">
                        <span className="text-gray-500 block">ITCL</span>
                        <span className={`inline-flex mt-0.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${badgeClass(shipment.ogcl_transfer_status)}`}>
                          {shipment.ogcl_transfer_status || '—'}
                        </span>
                      </div>
                    </div>
                    {shipment.status !== 'NOT_FOUND' && (
                      <div className="pt-3 border-t border-gray-100 flex justify-end">
                        {renderShipmentActions(shipment, `mobile-${shipment.shipment_id}`)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {revertedShipments.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden min-w-0">
            <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex items-center justify-between gap-3 min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-secondary truncate">Removed from request</h2>
              <span className="text-xs sm:text-sm text-gray-500 shrink-0">
                {revertedShipments.length} item{revertedShipments.length === 1 ? '' : 's'}
              </span>
            </div>
            <ul className="divide-y divide-gray-100">
              {revertedShipments.map((shipment) => (
                <li key={`reverted-${shipment.shipment_id}-${shipment.reverted_at || ''}`} className="px-3 sm:px-5 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-secondary break-all">{shipment.shipment_id}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Returned to booked. Note: {shipment.revert_note || '—'}
                      </p>
                    </div>
                    {shipment.status !== 'NOT_FOUND' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={Eye}
                        mobileIconOnly
                        onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                      >
                        View
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <OgclReviewConfirmModal
        open={reviewModal.open}
        action={reviewModal.action}
        requestId={request.request_id}
        loading={acting}
        onClose={closeReview}
        onConfirm={confirmReview}
      />
      <OgclUnloadNoteModal
        open={noteModal.open}
        action={noteModal.action}
        shipmentIds={noteModal.shipmentIds}
        loading={Boolean(unloadingId)}
        onClose={closeNoteModal}
        onConfirm={confirmNoteAction}
      />
    </DashboardLayout>
  );
};

export default ViewOgclTransferRequest;
